import { useFormik } from "formik";
import React, { ChangeEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Alert,
  Col,
  Form,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
} from "reactstrap";
import * as Yup from "yup";

import { ToastContainer } from "react-toastify";
import {
  StudentV2Dto,
  UpdateStudentByAdminRequest,
} from "../../../../interfaces";
import { ActivityButton } from "../../../../components/Common";
import { CourseSelect, GradeSelect } from "../../../../components";
import { useUpdateStudentUserMutation } from "../../../../store/api/studentApi";

type TProps = {
  student: StudentV2Dto | null | undefined;
  show: boolean;
  onCloseClick: (event: any) => void;
};
export const AddEditStudentModal: React.FC<TProps> = ({
  student,
  show,
  onCloseClick,
}) => {
  //#region hooks
  const { t, i18n } = useTranslation();
  //#endregion

  //#region internal states
  const [request, setRequest] = useState<UpdateStudentByAdminRequest>({
    course: student?.courseId || "",
    grade: student?.gradeId || "",
  });
  //#endregion

  //#region RTK Query hooks
  const [
    updateStudentUser,
    { isLoading: loadingUpdateStudentUser, error: errorUpdateStudentUser },
  ] = useUpdateStudentUserMutation();
  //#endregion

  //#region form
  const form = useFormik<UpdateStudentByAdminRequest>({
    enableReinitialize: true,
    initialValues: {
      id: student?.id || "",
      course: request.course || student?.courseId || "",
      grade: request.grade || student?.gradeId || "",
    },
    validationSchema: Yup.object({
      id: Yup.string().required(),
      course: Yup.string().required(),
      grade: Yup.string().required(),
    }),
    onSubmit: async (values) => {
      if (student?.id) {
        try {
          await updateStudentUser({
            id: student.id,
            data: { ...values },
          }).unwrap();
          onClose();
        } catch (err) {}
      }
    },
  });
  //#endregion

  //#region handlers
  const onClose = () => {
    form.resetForm();
    onCloseClick(!show);
  };

  const onChangeCourse = (value: string) => {
    setRequest({ ...request, course: value, grade: "" });
  };

  const onChangeGrade = (value: string) => {
    setRequest({ ...request, grade: value });
  };

  //#endregion

  return (
    <React.Fragment>
      <Modal
        id="addEdit"
        isOpen={show}
        toggle={() => {
          onClose();
        }}
        centered
      >
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
            return false;
          }}
          action="#"
        >
          <ModalHeader
            className="p-3"
            toggle={() => {
              onClose();
            }}
          >
            {t("editItem", { item: "user" })}
          </ModalHeader>
          {errorUpdateStudentUser && (
            <Alert color="danger" className="rounded-0 mb-0">
              <p className="mb-0">
                {(errorUpdateStudentUser as any)?.data?.message ||
                  "An error occurred"}
              </p>
            </Alert>
          )}
          <ModalBody>
            <Row className="g-2 mb-3">
              <Col>
                <span>
                  {student?.name[i18n.language === "ar" ? "ar" : "en"] || "-"}
                </span>
              </Col>
            </Row>
            <Row className="g-2 mb-3">
              <Col>
                <a
                  style={{ cursor: "pointer", direction: "ltr" }}
                  href={`tel:+${student?.phoneNumber?.countryCode}${student?.phoneNumber?.phoneNumber}`}
                >
                  <u
                    style={{ direction: "ltr" }}
                  >{`+${student?.phoneNumber?.countryCode}${student?.phoneNumber?.phoneNumber}`}</u>
                </a>
              </Col>
            </Row>
            <Row className="g-2 mb-3">
              <Col sm={12} className="mb-2">
                <div className="search-box">
                  <CourseSelect
                    value={form.values.course}
                    placeholder={t("course")}
                    type="select"
                    id="courseId"
                    name="courseId"
                    className="form-select"
                    aria-label={t("course")}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      onChangeCourse(event.target.value)
                    }
                  ></CourseSelect>
                </div>
              </Col>
            </Row>
            <Row>
              <Col sm={12} className="mb-2">
                <div className="search-box">
                  <GradeSelect
                    courseId={form.values.course}
                    value={form.values.grade}
                    placeholder={t("grade")}
                    type="select"
                    id="gradeId"
                    name="gradeId"
                    className="form-select"
                    aria-label={t("grade")}
                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                      onChangeGrade(event.target.value)
                    }
                  ></GradeSelect>
                </div>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Link
              to={"#"}
              onClick={onClose}
              className="btn btn-link link-success fw-medium"
            >
              <i className="ri-close-line me-1 align-middle"></i> {t("close")}
            </Link>
            <ActivityButton
              title="save"
              inProgressTitle="saving"
              color="success"
              className="btn btn-success"
              type="submit"
              inProgress={loadingUpdateStudentUser}
              disabled={loadingUpdateStudentUser}
            ></ActivityButton>
          </ModalFooter>
        </Form>
      </Modal>
    </React.Fragment>
  );
};
